import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrg } from "@/hooks/useOrg";
import { useStudios } from "@/hooks/useStudios";
import { useOrgEndUsers } from "@/hooks/useOrgEndUsers";
import { useOrgUserCounts } from "@/hooks/useOrgUserCounts";
import { sliceScopeNote } from "@/lib/orgUserSlice";
import { PortalDashboard } from "@/components/portal/PortalDashboard";
import { OrganizationContextUnavailable } from "@/components/portal/OrganizationContextUnavailable";

const STUDIO_SLICE_LIMIT = 200;

export function OrgAdminDashboard() {
  const navigate = useNavigate();
  const { orgUser, role } = useAuth();
  const orgId = orgUser?.orgId ?? undefined;
  const orgState = useOrg(orgId);
  const studiosState = useStudios(orgId);
  const userRecordState = useOrgEndUsers(orgId);
  const counts = useOrgUserCounts(orgId);

  const org = orgState.org?.id === orgId ? orgState.org : null;
  const studios = useMemo(
    () => studiosState.studios.filter((studio) => studio.orgId === orgId),
    [orgId, studiosState.studios]
  );
  const userRecordCounts = useMemo(() => {
    const perStudio = new Map<string, number>();
    for (const userRecord of userRecordState.endUsers) {
      perStudio.set(userRecord.studioId, (perStudio.get(userRecord.studioId) ?? 0) + 1);
    }
    return perStudio;
  }, [userRecordState.endUsers]);
  const workspaces = useMemo(
    () => studios.map((studio) => ({ id: studio.id, name: studio.name, status: studio.status })),
    [studios]
  );
  const studiosAreCurrent =
    studiosState.loading || Boolean(studiosState.error) || studiosState.studios.every((studio) => studio.orgId === orgId);
  const workspacesScopeNote = studios.length >= STUDIO_SLICE_LIMIT
    ? `Showing up to ${STUDIO_SLICE_LIMIT} workspaces. This view does not prove that the list is complete.`
    : null;
  const hasOrgContext =
    (role === "ORG_OWNER" || role === "ORG_ADMIN") && Boolean(orgId);

  if (!hasOrgContext) {
    return <OrganizationContextUnavailable />;
  }

  return (
    <PortalDashboard
      organizationName={org?.name || (orgState.loading ? "Organization" : "Organization unavailable")}
      adminName={orgUser?.name || "Administrator"}
      role={role}
      workspaces={workspaces}
      workspacesLoading={studiosState.loading || !studiosAreCurrent}
      workspacesError={studiosState.error}
      workspacesScopeNote={workspacesScopeNote}
      totalUsers={counts.totalUsers}
      activeUsers={counts.activeUsers}
      countsLoading={counts.loading}
      countsError={counts.error}
      perWorkspaceUserCounts={userRecordCounts}
      userSliceLoading={userRecordState.loading}
      userSliceError={userRecordState.error}
      userScopeNote={sliceScopeNote(userRecordState.limitReached)}
      onRefreshCounts={counts.refresh}
      onOpenWorkspace={(studioId) => navigate(`/org/studios/${studioId}`)}
      onViewWorkspaces={() => navigate("/org/studios")}
      onViewUsers={() => navigate("/org/users")}
      onViewTeam={() => navigate("/org/team")}
    />
  );
}
